import React from 'react';
import {Modal, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import {useTranslation} from 'react-i18next';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {ButtonLayoutContext} from '@/shared/ui/button-layout';
import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {layout, radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The bottom sheet: the standard way a phone app asks a short question without
 * leaving the screen behind it.
 *
 * Two details do most of the work: the GRABBER (the small bar at the top — it is what
 * tells a thumb the sheet can be dismissed downward, even here where the gesture
 * itself is a backdrop tap) and the bottom safe-area padding, without which the last
 * option sits under the home bar on every modern handset.
 *
 * On a wide window the sheet is the `Modal` ui-kit: окно — прибор, поставленный
 * поверх работы. Прямые углы, рамка `border-strong`, плотная тень; полоса заголовка
 * 36pt на `surface-inset` с линией под ней и крестиком, который под мышью
 * становится красным — как кнопка закрытия окна.
 */
const styles = StyleSheet.create({
  backdrop: {flex: 1, justifyContent: 'flex-end', alignItems: 'center'},
  backdropDesktop: {justifyContent: 'center', padding: spacing(5)},
  dialog: {
    maxWidth: 560,
    maxHeight: '90%',
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 0,
    paddingTop: 0,
    overflow: 'hidden',
  },
  dialogHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    minHeight: 36,
    paddingLeft: spacing(3),
    paddingRight: spacing(2),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  dialogHeadText: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: spacing(2),
    paddingVertical: spacing(1) + 2,
  },
  dialogClose: {
    width: 26,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.xs,
  },
  dialogBody: {padding: spacing(4)},
  sheet: {
    width: '100%',
    maxWidth: layout.maxContentWidth,
    // A sheet sizes to its content, so a long one (an explanation with a dozen terms)
    // would grow past the top of the screen and clip its own head. Capping it hands the
    // overflow to the ScrollView below, which is what should have it.
    maxHeight: '86%',
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing(4),
    paddingTop: spacing(2),
  },
  grabber: {
    alignSelf: 'center',
    width: 36,
    height: 3,
    borderRadius: 1,
    marginBottom: spacing(3),
  },
  head: {gap: spacing(1), marginBottom: spacing(3)},
  body: {gap: spacing(2)},
});

/** Курсор над крестиком или палец на нём. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

export function Sheet({
  visible,
  onClose,
  title,
  subtitle,
  children,
  testID,
}: {
  visible: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  testID?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const insets = useSafeAreaInsets();
  const {isDesktop} = useBreakpoint();

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isDesktop ? 'fade' : 'slide'}
      statusBarTranslucent
      onRequestClose={onClose}>
      <Pressable
        testID={testID ? `${testID}-backdrop` : 'sheet-backdrop'}
        style={[styles.backdrop, isDesktop && styles.backdropDesktop, {backgroundColor: p.overlay}]}
        onPress={onClose}>
        {/* Swallow the press so tapping the sheet itself never dismisses it. */}
        <Pressable
          testID={testID}
          onPress={() => {}}
          style={[
            styles.sheet,
            shadows.float,
            {
              backgroundColor: p.elevated,
              borderColor: p.borderStrong,
              paddingBottom: insets.bottom + spacing(4),
            },
            isDesktop && [styles.dialog, {borderColor: p.borderStrong, paddingBottom: 0}],
          ]}>
          {isDesktop ? null : (
            <View style={[styles.grabber, {backgroundColor: p.borderStrong}]} />
          )}

          {isDesktop ? (
            <View
              style={[
                styles.dialogHead,
                {backgroundColor: p.inset, borderBottomColor: p.border},
              ]}>
              <View style={styles.dialogHeadText}>
                {title ? <AppText variant="subtitle">{title}</AppText> : null}
                {subtitle ? <AppText variant="micro">{subtitle}</AppText> : null}
              </View>
              <Pressable
                testID={testID ? `${testID}-close` : undefined}
                accessibilityRole="button"
                accessibilityLabel={t('actions.close')}
                onPress={onClose}
                hitSlop={6}
                style={state => [
                  styles.dialogClose,
                  isHot(state) && {backgroundColor: p.destructive},
                ]}>
                {state => (
                  <Icon
                    name="close"
                    size={14}
                    color={isHot(state) ? p.destructiveForeground : p.mutedForeground}
                  />
                )}
              </Pressable>
            </View>
          ) : title ? (
            <View style={styles.head}>
              <AppText variant="title">{title}</AppText>
              {subtitle ? <AppText variant="caption">{subtitle}</AppText> : null}
            </View>
          ) : null}

          <ScrollView
            bounces={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[styles.body, isDesktop && styles.dialogBody]}>
            {/* Лист шириной с телефон на любом окне — кнопки в нём растягиваются. */}
            <ButtonLayoutContext.Provider value="stretch">{children}</ButtonLayoutContext.Provider>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
